import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Api from '../utils/Api';
import { useAuth } from './AuthContext';
import { useError } from './ErrorContext';
import { useLoading } from './LoadingContext';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';

const EquipmentTypeContext = createContext();
 
export const useEquipmentTypes = () => {
  const context = useContext(EquipmentTypeContext);
  if (!context) {
    throw new Error('useEquipmentTypes must be used within a EquipmentTypeProvider');
  }
  return context;
};

export const EquipmentTypeProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hideLoading } = useLoading();
  const { getToken } = useAuth();
  const [ setApi] = useState(null);
  const { handleApiError, formErrors, clearErrors } = useError();
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/equipment-types');

  const getEquipmentTypes = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
   
    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if(filters?.q){
        query = '&q='+filters?.q;
        
      } else{
        const q = searchParams.get('q') || '';
        query = '&q='+q;
      }
      if(filters?.active){
        query += '&active='+filters?.active;
      } else{
        const active = searchParams.get('active') || '';
        query += '&active='+active;
      }
      setPaginate(true)
      let response = await apiInstance.call('equipment-types?page=' + page+'&paginate='+paginate +''+query , 'GET');
      if(filters?.all){
        query = '';
        setPaginate(false)
        if(filters?.active){
          if(filters?.active === true){
            query += 'active=1';
          }else{
            query += 'active=0';
          }
         
        }
        response = await apiInstance.call('equipment-types?'+query);  
      }
      
      if (response.status === 200) {
       // setPaginate(response.data.paginate)
        setEquipmentTypes(response.data.equipmentTypes || []);
       
        
        if(filters){
          return response.data.equipmentTypes;
        }
      } else {
        
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch equipment types information'
        });
      }
    } catch (error) {
     
    } finally {
      
    }
  }, [handleApiError, getToken,paginate]);

  const getEquipmentTypeByIdApi = useCallback(async (id, filters = {}) => {
    
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call(`equipment-types/${id}/edit`, 'GET', null, true, false);
   
    if (response.status === 200) {
      
      let equipmentTypes = {'data':[response.data.equipmentType]}
      setEquipmentTypes(equipmentTypes || []);
      
    } 
  }, [getToken]);

  useEffect(() => {
    
    const handleLoad = async() => {
      const currentPath = window.location.pathname;
      
      if(currentPath.includes(getRoutePath('/equipment-types/edit/'+id))){
        if (getToken()) {
        
          
          await getEquipmentTypeByIdApi(id);
        }
      }
    };

    // Add onload listener
    window.addEventListener('load', handleLoad);

    // Cleanup listener
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath, id, getToken, getEquipmentTypeByIdApi, searchParams,setApi]);

  // Initialize API and load initial data
  useEffect(() => {
    const initializeApi = async () => {
      try {
     
        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
         
          await getEquipmentTypes(page);
        }
      } catch (error) {
        hideLoading();
      }
    };
   
    if(initialized){
      initializeApi();
    }
  }, [getToken, getEquipmentTypes, searchParams, initialized,hideLoading,setApi]);

    
  const addEquipmentType = async (data) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {

      let formData = new FormData();

      // Append image if provided
      if (data.image) {
        formData.append('image', data.image);
      }
      
      // Append the rest of the data as JSON
      formData.append('data', JSON.stringify(data));

      const response = await apiInstance.call('equipment-types', 'POST', formData, true,true);
      
      if (response.status === 201 || response.status === 200) {
        // Refresh the equipment types list
        await getEquipmentTypes();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add Equipment Type'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to create equipment type'
      });
      return { status: 500, error: error.message };
    }
  };
  
  const getEquipmentTypeById = useCallback(async (id) => {
   // let type = equipmentTypes?.data?.find(equipmentType => Number(equipmentType.id) === Number(id));
 
    //return {'status':200,'data':type};
    
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`equipment-types/${id}`, 'GET', null, true);
      
      if (response.status === 200) {
        return { status: response.status, data: response.data.equipmentType };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to fetch equipment type details'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to fetch equipment type details'
      });
      return { status: 500, error: error.message };
    }
  }, [getToken, handleApiError]);

  const updateEquipmentType = async (id, updatedData) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      let formData = new FormData();

      // Append image if provided
      if (updatedData.image) {
        formData.append('image', updatedData.image);
      }
      
      // Append the rest of the data as JSON
      formData.append('data', JSON.stringify(updatedData));
      
      // Now send the request with formData only once
      const response = await apiInstance.call(`equipment-types/${id}`, 'POST', formData, true, true);
      
      if (response.status === 200) {
        // Refresh the equipment types list
        await getEquipmentTypes();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
             errors: response.error.validationErrors,
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update Equipment Type'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to update equipment type'
      });
      return { status: 500, error: error.message };
    }
  };

  const deleteEquipmentType = async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`equipment-types/${id}`, 'DELETE', null, true);
      
      if (response.status === 200) {
        // Refresh the equipment types list
        await getEquipmentTypes();
        navigate(getRoutePath('/equipment-types'));
        return { status: response.status, data: response.data };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to delete equipment type'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete equipment type'
      });
      return { status: 500, error: error.message };
    }
  };

  return (
    <EquipmentTypeContext.Provider
      value={{
        equipmentTypes,
        addEquipmentType,
        getEquipmentTypeById,
        updateEquipmentType,
        deleteEquipmentType,
        getEquipmentTypes,
        formErrors,
        clearErrors,
        setPaginate,
        paginate
      }}
    >
      {children}
    </EquipmentTypeContext.Provider>
  );
};

export default EquipmentTypeContext; 